<template>
  <vi-biz-table
    ref="table"
    :default-sort="defaultSortOpt"
    :data="spuList"
    :columns="spuTableColumns"
    :expand-row-keys="expandRowKeys"
    :class="{ emptyTable: !spuList.length }"
    row-key="spu_id"
    stripe
    class="table-no-hover"
    height="100%"
    @sortByCol="handleSortChange"
    @expand-change="expand"
  >
    <div slot="empty" class="prd-empty-tips">
      <SearchProductEmptySvg />
      {{ $t('module_creative_no_prd_in_search_result') }}
    </div>
  </vi-biz-table>
</template>

<script>
import HeaderProduct from './header-product.vue';
import HeaderSelect from './header-select.vue';
import HeaderStorefront from './header-storefront.vue';
import ProductDetail from './product-detail.vue';
import ProductSkuRows from './sku-rows.vue';
import ProductSearch from './product-search.vue';
import ProductSelect from './product-select.vue';
import { ProductStoreFrontStatus } from '@tt4b/ttam-enums';
import { i18n } from '../../../../i18n';
import numeral from 'numeral';
import SearchProductEmptySvg from '@tt4b/ttam-assets/src/svgs/creative/search-product-empty.svg';

const name = 'ttam-product-details-product-table';
export default {
  name,
  props: {
    spuList: {
      type: Array,
      default: () => [],
    },
    isEcomShop: {
      type: Boolean,
      default: true,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    selectedSpuSet: {
      type: Set,
      default: () => new Set(),
    },
    shopInfo: {
      type: Object,
      default: () => ({}),
    },
    inStorefrontFilter: {
      type: Number,
      default: 0,
    },
    isSingleSelect: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeaderProduct,
    HeaderSelect,
    HeaderStorefront,
    ProductSelect,
    ProductDetail,
    ProductSkuRows,
    ProductSearch,
    SearchProductEmptySvg,
  },
  data() {
    return {
      name,
      expandedRows: [],
      expandRow: {},
      expandRowKeys: [],
      defaultSortOpt: { total_sales: 1 },
    };
  },
  mounted() {
    // 如可以排序，进入table后，默认触发一次排序搜索
    if (this.sortable) {
      this.handleSortChange('total_sales', this.defaultSortOpt.total_sales);
    }
  },
  computed: {
    isAllSelected() {
      if (!this.spuList?.length) {
        return false;
      }
      return this.spuList.every(item => this.selectedSpuSet.has(item.spu_id));
    },
    isAllDisabled() {
      if (!this.spuList?.length) {
        return false;
      }
      return this.spuList.every(item => item.isDisabled);
    },
    isAllSelectIndeterminate() {
      if (!this.spuList?.length) {
        return false;
      }
      const hasSelected = this.spuList.some(item => this.selectedSpuSet.has(item.spu_id));
      return hasSelected && !this.isAllSelected;
    },
    spuTableColumns() {
      const cols = [
        {
          key: 'skuId',
          type: 'expand',
          component: ProductSkuRows,
          hidden: this.isEcomShop, // 1P 不支持查看 SKU
        },
        {
          width: this.isEcomShop ? 62 : 46,
          className: this.isEcomShop ? 'product-select-tbl-selection-1p' : '',
          selectable: () => true,
          renderString: !this.isSingleSelect ? (() => (
            <HeaderSelect
              isDisabled={this.isAllDisabled}
              isAllSelected={this.isAllSelected}
              isIndeterminate={this.isAllSelectIndeterminate}
              onChange={this.selectAllChange}
            />
          )) : undefined,
          formatter: row => (
            <ProductSelect
              isDisabled={row.isDisabled}
              shopInfo={this.shopInfo}
              product={row}
              value={this.isSelected(row)}
              isSingleSelect={this.isSingleSelect}
              onChange={val => this.selectChange(row, val)}
            />
          ),
        },
        {
          key: 'spuId',
          width: 356,
          renderString: () => (
            <HeaderProduct />
          ),
          formatter: row => (
            <ProductDetail
              product={row}
              disabled={false}
              picture={row.picture}
              title={row.title}
              id={`SPU ID: ${row.spu_id}`}
            />
          ),
        },
        {
          title: i18n.t('module_creative_product_column_price'),
          key: 'price',
          sortable: this.sortable,
          width: 150,
          align: 'right',
          formatter: row => {
            const { price = '' } = row || {};
            // 如果区间是相等的只展示一个
            const [min = '', max = ''] = (price?.price_range || '').split('-');
            const range = min === max ? this.separator(min) : `${this.separator(min)}-${this.separator(max)}`;
            return `${range} ${price?.price_unit}`;
          },
        },
        {
          title: i18n.t('module_creative_product_column_sales'),
          key: 'total_sales',
          width: 150,
          align: 'right',
          sortable: this.sortable,
          hidden: !this.sortable, // 没开白 不可排序不可展示“销量”列
        },
        {
          title: i18n.t('module_creative_product_column_category'),
          key: 'category',
          formatter: row => row?.category?.name || '-',
        },
        {
          // 是否在橱窗中 当前仅支持3P
          key: 'storefront_status',
          align: 'left',
          width: 250,
          hidden: this.isEcomShop,
          renderString: () => (
            <HeaderStorefront filter={this.inStorefrontFilter} onChange={this.inStorefrontFilterChange} />
          ),
          formatter: row => {
            const status = row?.storefront_status;
            if (status === ProductStoreFrontStatus.InStoreFront) {
              return i18n.t('module_creative_in_storefront');
            }
            if (status === ProductStoreFrontStatus.NotInStoreFront) {
              return i18n.t('module_creative_not_in_storefront');
            }
            return '';
          },
        },
      ].filter(item => !item.hidden);
      return cols;
    },
  },
  methods: {
    selectAllChange(val) {
      this.$emit('selectAllChange');
    },
    selectChange(row) {
      this.$emit('selectChange', row);
    },
    inStorefrontFilterChange(val) {
      this.$emit('inStorefrontFilterChange', val);
    },
    separator(value, format = '0,0.[00]') {
      return numeral(value).format(format);
    },
    isSelected(row) {
      return this.selectedSpuSet.has(row?.spu_id);
    },
    expand(row, expandRowKeys) {
      this.expandedRows = expandRowKeys;
    },
    // 按列排序, reverse 0: 升序, 1: 降序
    handleSortChange(key, reverse) {
      this.$emit('sort', key, reverse);
    },
  },
};
</script>

<style lang="scss">
.product-select-tbl-selection-1p .cell {
  padding-left: 32px !important;
}
</style>
